import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { DOCUMENT_METADATA_FIELD_CONFIG } from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import { PAGE_METADATA_FIELD_CONFIG } from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'
import type { MetadataDisplayField } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'

const DOCUMENT_TYPE_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  'work-order': msg`Work order`,
  'p&id': msg`P&ID`,
  'electrical schematic': msg`Electrical Schematic`,
  'piping isometric': msg`Piping Isometric`,
  legend: msg`Legend`,
  'pipe support': msg`Pipe Support`,
  'general arrangement': msg`General Arrangement`,
  'electrical one line': msg`Electrical One Line`,
  'electrical three line': msg`Electrical Three Line`,
  'logic diagram': msg`Logic Diagram`,
  'civil/structural details': msg`Civil/Structural Details`,
  other: msg`Other`,
}

/**
 * Display label for a stored `document_type` value. Filters and sorting keep
 * the stored value; only rendering goes through this.
 */
export function documentTypeLabel(
  value: string,
  translate: (descriptor: MessageDescriptor) => string
): string {
  const known = DOCUMENT_TYPE_LABELS[value.trim().toLowerCase()]
  if (known) return translate(known)
  return value.replace(
    /(^|[\s/])([a-z])/g,
    (_, boundary, letter) => `${boundary}${letter.toUpperCase()}`
  )
}

const DOCUMENT_TYPE_FIELD_LABELS = new Set<string>(
  [...DOCUMENT_METADATA_FIELD_CONFIG, ...PAGE_METADATA_FIELD_CONFIG].flatMap(
    (field) =>
      'key' in field &&
      (field.key === 'document_type' || field.key === 'page_classification')
        ? [field.label]
        : []
  )
)

/** Read-only metadata rows with the type value swapped for its display label. */
export function withDocumentTypeLabels(
  fields: readonly MetadataDisplayField[],
  translate: (descriptor: MessageDescriptor) => string
): MetadataDisplayField[] {
  return fields.map((field) =>
    DOCUMENT_TYPE_FIELD_LABELS.has(field.label)
      ? { ...field, value: documentTypeLabel(field.value, translate) }
      : field
  )
}
